import { ISSUE_STATUSES, type IssueFilters, type IssueRow } from '@contract';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { useState } from 'react';
import { z } from 'zod';
import { DataTable } from '@/components/data-table';
import { DateRangeFilter } from '@/components/date-range-filter';
import { ExportButton } from '@/components/export-button';
import { EmptyState } from '@/components/feedback/empty-state';
import { QueryState } from '@/components/feedback/query-state';
import { SkeletonTable } from '@/components/feedback/skeletons';
import { productLabel, useTakeIt } from '@/components/issues/issue-work';
import { PageHeader } from '@/components/page-header';
import { Money } from '@/components/primitives/money';
import { StatusPill } from '@/components/primitives/status-pill';
import {
  OpenRecord,
  PersonCell,
  SummaryBand,
} from '@/components/queue/queue-parts';
import { SlaChip } from '@/components/queue/sla-chip';
import { useSheet, useSheetActions } from '@/components/sheet/use-sheet';
import { TabLinks } from '@/components/tab-links';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { rangeArgs } from '@/lib/date-range';
import { relativeTime } from '@/lib/format';
import { homeCurrency } from '@/lib/market';
import { adminQuery, useKeysetList } from '@/lib/query';
import { useHas } from '@/lib/staff-context';

const VIEWS = [
  { key: 'open', label: 'Open' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'due_soon', label: 'Due soon' },
  { key: 'mine', label: 'Mine' },
  { key: 'all', label: 'All' },
] as const;

type View = (typeof VIEWS)[number]['key'];

const PRODUCTS = ['send', 'add_money', 'momo_payout', 'bill'] as const;

/** `YYYY-MM-DDTHH:mm`, GMT. */
const RANGE_TEXT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

const searchSchema = z.object({
  view: z
    .enum(['open', 'overdue', 'due_soon', 'mine', 'all'])
    .catch('open')
    .default('open'),
  status: z.enum(ISSUE_STATUSES as [string, ...string[]]).optional(),
  owner: z.string().max(40).optional(),
  q: z.string().max(40).optional(),
  product: z.enum(PRODUCTS).optional(),
  from: z.string().regex(RANGE_TEXT).optional().catch(undefined),
  to: z.string().regex(RANGE_TEXT).optional().catch(undefined),
});

type IssueSearch = z.infer<typeof searchSchema>;

/** The report queue. The view and filters live in the URL. */
export const Route = createFileRoute('/_app/issues/')({
  validateSearch: searchSchema,
  component: IssuesPage,
});

const POLL_MS = 20_000;

function toArgs(search: IssueSearch): IssueFilters {
  return {
    view: search.view,
    status: search.status as IssueFilters['status'],
    owner: search.owner,
    product: search.product,
    q: search.q,
    ...rangeArgs(search),
  };
}

const column = createColumnHelper<IssueRow>();

const COLUMNS = [
  column.accessor('ref_no', {
    header: 'No.',
    cell: (info) => (
      <span className="font-mono text-muted-foreground">
        #{info.getValue()}
      </span>
    ),
  }),
  column.accessor('due_at', {
    header: 'Due',
    cell: ({ row }) => (
      <SlaChip
        at={row.original.due_at}
        resolvedAt={row.original.resolved_at}
        startedAt={row.original.created_at}
      />
    ),
  }),
  column.display({
    id: 'customer',
    header: 'Customer',
    meta: { sortValue: (row) => row.customer?.name ?? '' },
    cell: ({ row }) => <PersonCell customer={row.original.customer} />,
  }),
  column.accessor('note', {
    header: 'Report',
    cell: ({ row }) => (
      <OpenRecord
        sheetRef={{ type: 'issue', id: row.original.id }}
        label={`Open report: ${row.original.note.slice(0, 60)}`}
        className="line-clamp-2 max-w-md font-medium hover:underline"
      >
        {row.original.note}
      </OpenRecord>
    ),
  }),
  column.accessor((row) => productLabel(row.product), {
    id: 'product',
    header: 'Product',
  }),
  column.accessor((row) => row.amount_minor ?? -1, {
    id: 'amount',
    header: 'Amount',
    cell: ({ row }) =>
      row.original.amount_minor === null ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        <span className="block text-right">
          <Money minor={row.original.amount_minor} currency={homeCurrency()} />
        </span>
      ),
  }),
  column.accessor((row) => row.owner?.name ?? '', {
    id: 'owner',
    header: 'Owner',
    cell: ({ row }) => <OwnerCell issue={row.original} />,
  }),
  column.accessor('status', {
    header: 'Status',
    cell: (info) => <StatusPill status={info.getValue()} />,
  }),
  column.accessor('created_at', {
    header: 'Age',
    cell: (info) => (
      <span className="text-muted-foreground">
        {relativeTime(info.getValue())}
      </span>
    ),
  }),
];

/** The owner, or "Unassigned" with a Take it button for staff who work reports. */
function OwnerCell({ issue }: Readonly<{ issue: IssueRow }>) {
  const canWork = useHas('issues.work');
  const take = useTakeIt(issue.id);
  if (issue.owner) return <span>{issue.owner.name}</span>;
  return (
    <span className="flex items-center gap-2">
      <span className="text-muted-foreground">Unassigned</span>
      {canWork ? (
        <Button
          size="xs"
          variant="outline"
          pending={take.isPending}
          aria-label="Take this report"
          onClick={() => take.mutate()}
        >
          Take it
        </Button>
      ) : null}
    </span>
  );
}

function IssuesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const args = toArgs(search);
  // A refresh must not move rows under a cursor or a hand about to press one,
  // so the queue stops refreshing while the pointer or focus is in the table
  // or a record is open.
  const [engaged, setEngaged] = useState(false);
  const sheetOpen = useSheet().top !== undefined;
  const sheetActions = useSheetActions();
  const list = useKeysetList('issues.list', args, {
    rows: (data) => data.rows,
    refetchInterval: engaged || sheetOpen ? false : POLL_MS,
  });
  const workers = useQuery(adminQuery('issues.workers', {}));
  const counts = list.data?.counts;
  const set = (patch: Partial<IssueSearch>) =>
    void navigate({ search: { ...search, ...patch } });
  const filtered = Boolean(
    search.status ||
      search.owner ||
      search.product ||
      search.q ||
      search.from ||
      search.to,
  );

  return (
    <>
      <PageHeader
        title="Issue reports"
        description="Reports customers send from the app. Each is due within the hours set when it was filed."
        actions={<ExportButton source="issues.list" args={{ ...args }} />}
      />
      <SummaryBand
        label="Report figures"
        cells={[
          {
            label: 'Overdue',
            value: counts?.overdue,
            tone: 'danger',
            onSelect: () => set({ view: 'overdue' }),
            active: search.view === 'overdue',
          },
          {
            label: 'Due soon',
            value: counts?.due_soon,
            tone: 'warning',
            onSelect: () => set({ view: 'due_soon' }),
            active: search.view === 'due_soon',
          },
          {
            label: 'Open',
            value: counts?.open,
            onSelect: () => set({ view: 'open' }),
            active: search.view === 'open',
          },
          {
            label: 'Mine',
            value: counts?.mine,
            onSelect: () => set({ view: 'mine' }),
            active: search.view === 'mine',
          },
        ]}
      />
      <TabLinks
        tabs={VIEWS.map((view) => ({
          key: view.key,
          label:
            view.key !== 'all' && counts
              ? `${view.label} (${counts[view.key]})`
              : view.label,
        }))}
        active={search.view}
        render={(tab, domProps) => (
          <Link
            {...domProps}
            to="/issues"
            search={{ ...search, view: tab.key as View }}
          />
        )}
      />
      <div className="grid grid-cols-2 items-end gap-3 sm:flex sm:flex-wrap">
        <Input
          type="search"
          aria-label="Find by issue number"
          placeholder="#1042"
          className="w-full sm:w-40"
          defaultValue={search.q ?? ''}
          key={search.q ?? ''}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              set({ q: event.currentTarget.value.trim() || undefined });
            }
          }}
          onBlur={(event) =>
            set({ q: event.currentTarget.value.trim() || undefined })
          }
        />
        <Select
          label="Status"
          className="w-full sm:w-44"
          value={search.status ?? ''}
          onChange={(event) => set({ status: event.target.value || undefined })}
        >
          <option value="">Any</option>
          {ISSUE_STATUSES.map((status) => (
            <option key={status} value={status}>
              {status.replaceAll('_', ' ')}
            </option>
          ))}
        </Select>
        <Select
          label="Owner"
          className="w-full sm:w-48"
          value={search.owner ?? ''}
          onChange={(event) => set({ owner: event.target.value || undefined })}
        >
          <option value="">Anyone</option>
          <option value="none">Nobody yet</option>
          {workers.data?.map((worker) => (
            <option key={worker.id} value={worker.id}>
              {worker.name}
            </option>
          ))}
        </Select>
        <Select
          label="Product"
          className="w-full sm:w-40"
          value={search.product ?? ''}
          onChange={(event) =>
            set({
              product: (event.target.value ||
                undefined) as IssueSearch['product'],
            })
          }
        >
          <option value="">Any</option>
          {PRODUCTS.map((product) => (
            <option key={product} value={product}>
              {productLabel(product)}
            </option>
          ))}
        </Select>
        <DateRangeFilter
          noun="Raised"
          value={search}
          onChange={(range) => set(range)}
        />
        {filtered ? (
          <Button
            variant="ghost"
            onClick={() =>
              set({
                status: undefined,
                owner: undefined,
                product: undefined,
                q: undefined,
                from: undefined,
                to: undefined,
              })
            }
          >
            Clear filters
          </Button>
        ) : null}
      </div>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: only tracks where the pointer and focus are, to pause refreshing. */}
      <div
        onPointerEnter={() => setEngaged(true)}
        onPointerLeave={() => setEngaged(false)}
        onFocus={() => setEngaged(true)}
        onBlur={() => setEngaged(false)}
      >
        <QueryState
          query={list.query}
          label="reports"
          staleAfterMs={3 * POLL_MS}
          skeleton={<SkeletonTable columns={9} />}
        >
          {() =>
            list.rows?.length === 0 ? (
              <EmptyState
                variant={
                  filtered || search.view === 'all' ? 'filtered' : 'clear'
                }
                title={
                  filtered
                    ? 'No reports match these filters.'
                    : 'No reports in this view.'
                }
                body={
                  filtered ? undefined : 'Nothing here needs anyone right now.'
                }
              />
            ) : (
              <DataTable
                id="issues"
                columns={COLUMNS}
                data={list.rows}
                more={list.more}
                rowLabel={(row) => `Open report ${row.id.slice(0, 8)}`}
                onRowClick={(row, rowEl) =>
                  sheetActions.open(
                    { type: 'issue', id: row.id },
                    { fromRowEl: rowEl.querySelector('button') ?? rowEl },
                  )
                }
              />
            )
          }
        </QueryState>
      </div>
    </>
  );
}
